import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MultiOption, MultiOptions, MultiSubOption, MultiSubOptions } from './multi-select';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { User } from '../_models';
import { StorageService } from '../_services';
import { dimensionGroupModels } from '../reports/utils/constants';

enum FilterOption {
  TimePeriod = 'time_period',
  MerchantId = 'merchant_id',
}

enum FilterOptionType {
  Dropdown = 'DROPDOWN',
  Input = 'INPUT',
  Comparison = 'COMPARISON',
}

enum SelectedOption {
  Range = 'range',
  EqualTo = 'e'
}

@Component({
  selector: 'app-custom-multi-select',
  templateUrl: './custom-multi-select.component.html',
  styleUrls: ['./custom-multi-select.component.scss'],
})
export class CustomMultiSelectComponent implements OnInit, OnChanges {
  @Input() options: MultiOptions = [];
  @Input() isFilterOption: boolean;
  @Input() isV3Report: boolean = false;
  @Output() onGetProducts = new EventEmitter<{
    searchTerm: string;
    nextPage: boolean;
  }>();
  @Output() selectedOptionsChange = new EventEmitter<{
    option: MultiOption;
    subOption: MultiSubOption;
  }>();
  @Output() selectedInputsChange = new EventEmitter<{
    option: MultiOption;
    value: string;
  }>();
  @Output() selectedComparisonInputsChange = new EventEmitter<{
    option: MultiOption;
    value: string;
    comparingOption: string;
  }>();
  user: User;
  private _destroy$ = new Subject<void>();
  private searchInputSubject = new Subject<string>();
  isDropdownVisible = false;
  filteredOptions: MultiOptions = [];
  subOptions: MultiSubOptions = [];
  filteredSubOptions: MultiSubOptions = [];
  selectedMainOption: MultiOption = null;
  selectedSubOption: MultiOption | null = null;
  thirdLevelSearchTerm: string = '';
  thirdLevelFilteredOptions: MultiSubOption[] = [];
  thirdLevelTopOffset: number = 0;
  comparisonDropDownOption: MultiOption;
  comparisonDropDownInput: string;
  comparisonBetweenInput: string;
  selectedOption = SelectedOption;
  filterOptionType = FilterOptionType;

  constructor(protected storageService: StorageService) {}

  ngOnInit() {
    this.user = this.storageService.get('currentUser');
    this.searchInputSubject
      .pipe(
        debounceTime(800), // adjust the debounce time as needed
        distinctUntilChanged(),
        takeUntil(this._destroy$)
      )
      .subscribe((searchTerm) => {
        this.handleSubSearch(searchTerm);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options && changes.options.currentValue) {
      this.filteredOptions = [...this.options];
    }
  }

  toggleDropdown() {
    this.isDropdownVisible = !this.isDropdownVisible;
    if (!this.isDropdownVisible) {
      this.resetPanelState();
    }
  }

  containsDimensionGroup(): boolean {
    return this.options.find((opt) => dimensionGroupModels.includes(opt.model as string)) !== undefined;
  }

  closeMultiSelect() {
    this.isDropdownVisible = false;
    this.resetPanelState();
  }

  private resetPanelState() {
    this.filteredOptions.forEach((option) => {
      option.selected = false;
    });
    this.selectedMainOption = null;
    this.selectedSubOption = null;
    this.thirdLevelFilteredOptions = [];
    this.thirdLevelSearchTerm = '';
    this.thirdLevelTopOffset = 0;
  }

  toggleSubDropdown(option: MultiOption) {
    this.filteredOptions.map((option) => (option.selected = false));
    option.selected = !option.selected;
  }

  onSearchInputChange(searchTerm: string) {
    if (searchTerm.trim() === '') {
      this.filteredOptions = [...this.options];
    } else {
      this.filteredOptions = this.options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
  }

  onOptionSelected(option: MultiOption) {
    if (!option.loading) {
      this.subOptions = option.options;
      this.filteredSubOptions = option.options;
      // Reset 3rd level state so switching categories never carries over a previous panel
      this.selectedSubOption = null;
      this.thirdLevelFilteredOptions = [];
      this.thirdLevelSearchTerm = '';
      this.thirdLevelTopOffset = 0;
      this.toggleSubDropdown(option);
      this.selectedMainOption = option;
    }
  }

  updateFilterOptionState() {
    const activeFilters = this.options.filter((option) => option.activeCount > 0);

    if (activeFilters.length >= 10) {
      this.options.forEach((currentOption) => {
        if (currentOption.options && currentOption.activeCount === 0)
          currentOption.options.forEach((sub) => {
            currentOption.disabled = true;
            if (!sub.selected) {
              sub.disabled = true;
            }
          });
        else if (!currentOption.selectedOption) {
          if (currentOption.searchTerm) {
            currentOption.disabled = false;
          } else {
            currentOption.disabled = true;
          }
        }
      });
    } else {
      this.options.forEach((currentOption) => {
        if (!currentOption.unavailable) {
          currentOption.disabled = false;
        }
        if (currentOption.options) {
          currentOption.options.forEach((sub) => {
            if (!sub.unavailable) {
              sub.disabled = false;
            }
          });
        }
      });
    }
  }

  onSubOptionSelected(option: MultiOption, subOption: MultiSubOption) {
    subOption.selected = !subOption.selected;

    const isRatioField = typeof subOption.model === 'string' && subOption.model.includes('ratio');

    if (subOption.selected) {
      if (isRatioField) {
        this.onRatioChange(subOption.selected);
      }
    } else if (isRatioField) {
      this.onRatioChange(subOption.selected);
    }
    const selectedCount = this.options.reduce((count, option) => {
      return count + (option.options && option.options.filter((suboption) => suboption.selected).length);
    }, 0);

    option.activeCount = subOption.selected ? ++option.activeCount : --option.activeCount;
    if (option.activeCount < 0) {
      option.activeCount = 0;
    }

    if (!this.isFilterOption) {
      // V3 reports cap dimensions at 5; all other reports use 10
      const dimensionLimit = this.isV3Report ? 5 : 10;
      if (selectedCount >= dimensionLimit) {
        this.options.forEach((option) => {
          option.options.forEach((sub) => {
            if (!sub.selected) {
              sub.disabled = true;
            }
          });
        });
      } else {
        this.options.forEach((option) => {
          if (option.options) {
            option.options.forEach((sub) => {
              if (!sub.unavailable) {
                sub.disabled = false;
              }
            });
          }
        });
      }
    } else {
      this.updateFilterOptionState();
    }

    this.selectedOptionsChange.emit({ option, subOption });
  }

  clearSubLevelOptions(event: Event, option: MultiOption) {
    event.stopPropagation();
    const allOptions = option.options as MultiOption[];
    allOptions.forEach((sub: MultiOption) => {
      if (sub.type === FilterOptionType.Input && sub.activeCount > 0) {
        // Temporarily set selectedSubOption so onThirdLevelInputChange can update activeCount and emit correctly
        this.selectedSubOption = sub;
        this.onThirdLevelInputChange('');
        this.selectedSubOption = null;
      } else if (sub.options) {
        (sub.options as MultiSubOption[]).forEach((nested: MultiSubOption) => {
          if (nested.selected) {
            this.onSubOptionSelected(sub, nested);
          }
        });
      } else {
        if (sub.selected) {
          this.onSubOptionSelected(option, sub as MultiSubOption);
        }
      }
    });
    // Recalculate parent category activeCount so the 1st-level checkmark clears
    option.activeCount = (option.options as MultiOption[]).reduce(
      (sum: number, ft: MultiOption) => sum + (ft.activeCount || 0),
      0
    );
  }

  clearThirdLevelOptions(event: Event) {
    event.stopPropagation();
    if (this.selectedSubOption.type === FilterOptionType.Input) {
      this.onThirdLevelInputChange('');
    } else {
      const allOptions = (this.selectedSubOption.options || []) as MultiSubOption[];
      allOptions.forEach((opt: MultiSubOption) => {
        if (opt.selected) {
          this.onSubOptionSelected(this.selectedSubOption as MultiOption, opt);
        }
      });
      const parentCategory = this.options.find(
        (o) => o.options && (o.options as MultiOption[]).includes(this.selectedSubOption)
      );
      if (parentCategory) {
        parentCategory.activeCount = (parentCategory.options as MultiOption[]).reduce(
          (sum: number, ft: MultiOption) => sum + (ft.activeCount || 0),
          0
        );
      }
    }
  }

  onAllSubOptionsSelected(option: MultiOption, filteredSubOptions: MultiSubOption[]) {
    // Check once if all are selected
    const allSelected = this.isAllSelected(filteredSubOptions);

    filteredSubOptions.forEach((sub) => {
      if (allSelected && sub.selected) {
        // Unselect all
        this.onSubOptionSelected(option, sub);
      } else if (!allSelected && !sub.selected) {
        // Select remaining
        this.onSubOptionSelected(option, sub);
      }
    });
  }

  isAllSelected(filteredSubOptions: MultiSubOption[]): boolean {
    return !!(filteredSubOptions && filteredSubOptions.length) && filteredSubOptions.every((s) => s.selected);
  }

  onFilterTypeSelected(event: Event, filterType: MultiOption) {
    event.stopPropagation();
    if (this.selectedSubOption === filterType) {
      this.selectedSubOption = null;
      this.thirdLevelFilteredOptions = [];
      this.thirdLevelSearchTerm = '';
      this.thirdLevelTopOffset = 0;
    } else {
      this.selectedSubOption = filterType;
      this.thirdLevelSearchTerm = '';
      this.thirdLevelFilteredOptions = filterType.options ? [...filterType.options] as MultiSubOption[] : [];
      const liEl = event.currentTarget as HTMLElement;
      const subDropdown = liEl.closest('.sub-dropdown-view');
      if (subDropdown) {
        const liRect = liEl.getBoundingClientRect();
        const parentRect = subDropdown.getBoundingClientRect();
        this.thirdLevelTopOffset = liRect.top - parentRect.top;
      }
    }
  }

  onThirdLevelSearch(searchTerm: string) {
    if (this.selectedSubOption.pagination) {
      this.onGetProducts.emit({ searchTerm, nextPage: false });
    } else {
      const allOptions = (this.selectedSubOption.options || []) as MultiSubOption[];
      if (searchTerm.trim() === '') {
        this.thirdLevelFilteredOptions = [...allOptions];
      } else {
        this.thirdLevelFilteredOptions = allOptions.filter((opt: MultiSubOption) =>
          opt.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
    }
  }

  onThirdLevelInputChange(value: string) {
    this.selectedSubOption.selected = !!value;
    this.selectedSubOption.searchTerm = value;
    this.selectedSubOption.activeCount = value ? 1 : 0;

    if (this.selectedMainOption) {
      this.selectedMainOption.activeCount = (this.selectedMainOption.options as MultiOption[]).reduce(
        (sum: number, ft: MultiOption) => sum + (ft.activeCount || 0),
        0
      );
    }

    // Keep parent category activeCount in sync for V3 nested structure
    const parentCategory = this.options.find(
      (o) => o.options && (o.options as MultiOption[]).includes(this.selectedSubOption)
    );
    if (parentCategory) {
      parentCategory.activeCount = (parentCategory.options as MultiOption[]).reduce(
        (sum: number, ft: MultiOption) => sum + (ft.activeCount || 0),
        0
      );
    }

    this.selectedInputsChange.emit({ option: this.selectedSubOption as MultiOption, value });
    // V3 filters use a 5-type limit enforced across nested categories; V2 uses flat updateFilterOptionState
    if (this.isV3Report && this.isFilterOption) {
      this.applyV3FilterLimit();
    } else {
      this.updateFilterOptionState();
    }
  }

  // V3 only: updates parent category activeCount after a 3rd-level dropdown value is toggled
  onThirdLevelOptionSelected(event: Event, filterType: MultiOption, value: MultiSubOption) {
    event.stopPropagation();
    this.onSubOptionSelected(filterType, value);
    const parentCategory = this.options.find((o) => o.options && (o.options as MultiOption[]).includes(filterType));
    if (parentCategory) {
      parentCategory.activeCount = (parentCategory.options as MultiOption[]).reduce(
        (sum: number, ft: MultiOption) => sum + (ft.activeCount || 0),
        0
      );
    }
    if (this.isV3Report && this.isFilterOption) {
      this.applyV3FilterLimit();
    }
  }

  // Enforces the V3 5-filter-type limit: disables inactive filter types and their values once the cap is reached
  private applyV3FilterLimit() {
    let activeFilterTypeCount = 0;
    this.options.forEach((category: MultiOption) => {
      if (category.options) {
        (category.options as MultiOption[]).forEach((ft: MultiOption) => {
          if (ft.activeCount > 0) {
            activeFilterTypeCount++;
          }
        });
      }
    });

    if (activeFilterTypeCount >= 5) {
      this.options.forEach((category: MultiOption) => {
        if (category.options) {
          (category.options as MultiOption[]).forEach((ft: MultiOption) => {
            if (ft.activeCount === 0) {
              ft.disabled = true;
              if (ft.options) {
                (ft.options as MultiSubOption[]).forEach((val: MultiSubOption) => {
                  val.disabled = true;
                });
              }
            }
          });
        }
      });
    } else {
      this.options.forEach((category: MultiOption) => {
        if (!category.unavailable) {
          category.disabled = false;
        }
        if (category.options) {
          (category.options as MultiOption[]).forEach((ft: MultiOption) => {
            if (!ft.unavailable) {
              ft.disabled = false;
            }
            if (ft.options) {
              (ft.options as MultiSubOption[]).forEach((val: MultiSubOption) => {
                if (!val.unavailable) {
                  val.disabled = false;
                }
              });
            }
          });
        }
      });
    }
  }

  onThirdLevelDropdownScroll(event: Event, filterType: MultiOption) {
    event.stopPropagation();
    if (!filterType.pagination) {
      return;
    }
    const el = event.target as HTMLElement;
    const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 1;
    if (atBottom && filterType.options && filterType.options.length > 49) {
      this.onGetProducts.emit({ searchTerm: filterType.searchTerm || '', nextPage: true });
    }
  }

  onRatioChange(selected = false) {
    this.options.forEach((otherOption) => {
      const timePeriodOption = otherOption.options.find((x) => x.model === FilterOption.TimePeriod);
      const merchantIdOption = otherOption.options.find((x) => x.model === FilterOption.MerchantId);

      if (timePeriodOption) {
        timePeriodOption.selected = selected;
        timePeriodOption.disabled = selected;
      }

      if (merchantIdOption) {
        merchantIdOption.selected = selected;
        merchantIdOption.disabled = selected;
      }
    });
  }

  onInputChange(option: MultiOption, value: string) {
    option.selected = true;
    this.selectedInputsChange.emit({ option, value });

    option.activeCount = value ? 1 : 0;

    this.updateFilterOptionState();
  }

  onComparisonDropdownChange(option: MultiOption) {
    this.comparisonDropDownOption = option;
    option.selected = true;
    option.activeCount = option.selectedOption ? 1 : 0;

    this.emitSelectedComparisonInputs(option, this.comparisonDropDownInput, this.comparisonBetweenInput || '');
    this.updateFilterOptionState();
  }

  onComparisonInputChange(value: string) {
    this.comparisonDropDownInput = value;
    this.emitSelectedComparisonInputs({ ...this.comparisonDropDownOption }, value, this.comparisonBetweenInput || '');
  }

  onComparisonBetweenInputChange(betweenValue: string) {
    this.comparisonBetweenInput = betweenValue;
    this.emitSelectedComparisonInputs({ ...this.comparisonDropDownOption }, this.comparisonDropDownInput, betweenValue);
  }

  private emitSelectedComparisonInputs(option: MultiOption, value: string, betweenValue?: string) {
    this.selectedComparisonInputsChange.emit({
      option,
      value,
      comparingOption: betweenValue || '',
    });
  }

  onSubSearchInputChange(searchTerm: string) {
    if (this.selectedMainOption.pagination) {
      this.searchInputSubject.next(searchTerm);
    } else {
      this.handleSubSearch(searchTerm);
    }
  }

  handleSubSearch(searchTerm: string) {
    if (this.selectedMainOption.pagination) {
      this.onGetProducts.emit({ searchTerm, nextPage: false });
    } else {
      if (searchTerm.trim() === '') {
        this.filteredSubOptions = [...this.subOptions];
      } else {
        this.filteredSubOptions = this.subOptions.filter((subOption) =>
          subOption.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
    }
  }

  onSubDropdownScroll(event: Event, options: MultiSubOptions) {
    if (!this.selectedMainOption.pagination) return;
    const subDropdown = event.target as HTMLElement;
    const atBottom = subDropdown.scrollHeight - subDropdown.scrollTop <= subDropdown.clientHeight + 1;

    if (atBottom && options.length > 49) {
      this.onGetProducts.emit({
        searchTerm: this.selectedMainOption.searchTerm,
        nextPage: true,
      });
    }
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
